import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT", "STORES"];
const EMPTY_ITEM = { item_id: "", quantity: "", unit_cost: "" };

export default function Receipts() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const { companies } = useCompany() || {};
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [history, setHistory] = useState([]);

  const load = async () => {
    try {
      const [itemRes, whRes, ledgerRes] = await Promise.all([
        api.get("/inventory/items"), api.get("/inventory/warehouses"), api.get("/inventory/stock-ledger"),
      ]);
      setItems(itemRes.data);
      setWarehouses(whRes.data);
      setHistory(ledgerRes.data.filter((l) => l.movement_type === "RECEIPT"));
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  useEffect(() => { load(); }, []);

  const itemLabel = (id) => { const i = items.find((it) => it.id === id); return i ? `${i.code} — ${i.name}` : id; };
  const warehouseName = (id) => warehouses.find((w) => w.id === id)?.name || id;

  return (
    <div className="space-y-6" data-testid="receipts-page">
      <PageHeader title="Receipts" description="Manual stock receipts — opening stock or non-PO material. GRNs raise stock automatically on posting." action={canWrite && <CreateReceiptDialog companies={companies} items={items} warehouses={warehouses} onCreated={load} />} />
      <div className="rounded-xl border border-border bg-card">
        {history.length === 0 ? (
          <EmptyState title="No manual receipts yet" testId="receipts-empty-state" />
        ) : (
          <div className="divide-y divide-border">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between p-4 text-sm" data-testid={`receipt-row-${h.id}`}>
                <span className="text-foreground">{itemLabel(h.item_id)} · {warehouseName(h.warehouse_id)}</span>
                <span className="font-mono text-xs text-muted-foreground">+{h.quantity} @ {h.unit_cost}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CreateReceiptDialog({ companies, items, warehouses, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", warehouse_id: "", receipt_date: "" });
  const [lines, setLines] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateLine = (idx, field, value) => setLines(lines.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/inventory/receipts", { ...form, items: lines });
      toast.success("Stock received");
      setForm({ company_id: "", warehouse_id: "", receipt_date: "" });
      setLines([{ ...EMPTY_ITEM }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-receipt-button"><Plus className="h-4 w-4" /> New Receipt</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Stock Receipt</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-receipt-form">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="receipt-company-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Warehouse</Label>
              <Select required value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                <SelectTrigger data-testid="receipt-warehouse-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" data-testid="receipt-date-input" value={form.receipt_date} onChange={set("receipt_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Items</Label>
            <div className="mt-1.5 space-y-2">
              {lines.map((l, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`receipt-item-row-${idx}`}>
                  <Select required value={l.item_id} onValueChange={(v) => updateLine(idx, "item_id", v)}>
                    <SelectTrigger className="flex-1" data-testid={`receipt-item-select-${idx}`}><SelectValue placeholder="Item" /></SelectTrigger>
                    <SelectContent>{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.code} — {it.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input required type="number" step="0.001" placeholder="Qty" className="w-24" value={l.quantity} onChange={(e) => updateLine(idx, "quantity", e.target.value)} data-testid={`receipt-item-qty-input-${idx}`} />
                  <Input required type="number" step="0.01" placeholder="Unit cost" className="w-28" value={l.unit_cost} onChange={(e) => updateLine(idx, "unit_cost", e.target.value)} data-testid={`receipt-item-cost-input-${idx}`} />
                  {lines.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setLines(lines.filter((_, i) => i !== idx))}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setLines([...lines, { ...EMPTY_ITEM }])} data-testid="add-receipt-item-button"><Plus className="h-3.5 w-3.5" /> Add item</Button>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-receipt-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Receive</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
